/******************************************** STYLING *********************************************/
const s = {
    ...require('../../input-util-components.scss'),
    ...require('./ci-form-input.scss'),
};

/************************************** THIRD-PARTY MODULES ***************************************/
import React from 'react';
import ReactDOM from 'react-dom';
import cn from 'classnames';

import {omit, IfTruthy} from 'mad-utils/lib/react';

/**************************************** PROJECT MODULES *****************************************/
import {translate, TinyText} from '@canimmunize/core-imports';

/**************************************** TYPE DEFINITIONS ****************************************/
export interface CIFormInputProps extends React.DOMAttributes<HTMLInputElement> {
    // Replaces all internal <input> elements' classes with given classes
    replaceAllInnerInputClassName?: string;
    onChange?: InputChangeHandler;
    onKeyDown?: EventHandler;
    required?: boolean;
    placeholder?: string;
    value: string;
    type?: Html.InputElement.Types;

    overrideClasses?: string;
    mergeClasses?: string;

    /**
     * Add CSS classes to outermost wrapper element (div surrounding the input
     * field tag, along with the description - if present)
     */
    classNameWrapper?: string;

    // If true, eliminate the bottom margin
    noBottomMargin?: boolean;
    // Maximum length string or number the element can contain
    maxLength?: number;
    id?: string;
    // Hide if true
    hidden?: boolean;
    // Text direction
    dir?: `ltr` | `rtl` | `auto`;
    // Special keyboard hotkey to access the element with
    accesskey?: string;
    // Order the element gets 'tabbed' to
    tabIndex?: number;
    // optional extra info about the element, shown as a tooltip on protracted mouseover
    title?: string;
    ciBorderColour?: string;
    autocomplete?: string;
    style?: React.CSSProperties;
    disabled?: boolean;
    /**
     * Text to (optionally) display below the input box
     */
    description?: string;
    widthClass?: string;
}

/************************************ CSS CLASS LIST FACTORIES ************************************/
/**
 * Use props given to input type along with default values to generate the full CSS class list
 * @param {string} overrideClasses Optional set of classes to completely replace the defaults
 * @param {string} mergeClasses Classes to add to the class list (either the default class
 *                                  list, or the list in overrideClasses if it was defined)
 * @param {boolean} noBottomMargin If true, remove the bottom margin classes
 * @param {string|number} topMargin size of the top margin above the input
 * @return {string} Complete merged list of CSS classes
 */
function inputFormCss(
    overrideClasses?: string,
    mergeClasses?: string,
    noBottomMargin = false,
    ciBorderColour?: string
): string {
    return (
        overrideClasses ||
        cn(
            noBottomMargin || `dmb-20--smb-10`,
            s[`remove-ios-shadow-from-input-boxes`],
            s[`ci-input-placeholder`],
            `dh-25--sh-21`, // <- Width/height
            `btw-0 blw-0 brw-0 bbw-0pt5`, // <- Border
            ciBorderColour || s[`ci-input-border-color`], // <- Border colour
            `better-helvetica fw-300 fs-14`, // <- Font/text
            `ta-left`, // <- Text alignment
            `dim-gray`, // <- Colour
            mergeClasses
        )
    );
}

/********************************************* EXPORT *********************************************/
/**
 * Render an input box with the proper styling
 *
 * Defaults to being a text box if type not given
 * All styles (css classes) can be eliminated by passing down an
 * overrideClasses prop
 * Alternatively, can merge more css classes into the defaults by passing a
 * mergeInStyles prop
 *
 * Example: <CIFormInput
 *              placeholder={translate(`title_name`).capitalize}
 *              onChange={props.events.nameChange}
 *              required={true}
 *              value={props.state.name}
 *              onKeyDown={Keypress(`enter`, props.events.submitCreateAccount)}
 *          />
 *          /\-- Creates a name input
 *
 * @param {Object} props (attributes):
 *        - {string}  value Box content
 *        - {boolean} required Can't submit form w/out a val in this input if true
 *        - {InputChangeHandler} onChange Function to run if the contents changes
 *        - {string} placeholder [OPTIONAL] Text to display if box is empty
 *        - {email|password|text} type [OPTIONAL] input box type [Default: `text`]
 *        - {string} overrideClasses [OPTIONAL] If included, replaces all
 *                                   default CSS classes w those in this prop
 *        - {string} mergeClasses [OPTIONAL] If included, these get merged
 *                                into the default CSS classes (or
 *                                overrideClasses if it's defined)
 *        - {boolean} noBottomMargin If true, remove the bottom margin classes
 *        - {JSX.Element} New form input React element
 *
 */
export const CIFormInput = (props: CIFormInputProps) => {
    const {type} = props;

    const ariaLabel =
        props[`aria-label`] ||
        props.title ||
        props.placeholder ||
        ((type === `email` || type === `password` || type === `month`) && translate(props.type)) ||
        (type === `tel` && translate(`mfa_phone`)) ||
        null;

    let {autocomplete} = props;

    if (!autocomplete) {
        autocomplete =
            props[`aria-label`] &&
            ((props[`aria-label`].toLowerCase() === `name` && `name`) ||
                (props[`aria-label`].toLowerCase() === `email` && `email`));
    }

    const noBottomMargin = !!(props.noBottomMargin || props.description);

    const widthClass = props.widthClass || cn(`ci-card-content-width`);

    return (
        <div className={cn(s[`ci-form-wrapper`], props.classNameWrapper)}>
            <input
                id={props.id || null}
                type={props.type || `text`}
                role="textbox"
                placeholder={typeof props.placeholder === `string` ? props.placeholder : ``}
                autoComplete={type === `email` ? `email` : type === `password` ? `password` : `on`}
                required={typeof props.required === `undefined` || props.required == null}
                style={{
                    ...props.style,
                    ...(noBottomMargin && {marginBottom: noBottomMargin && 0}),
                }}
                className={
                    props.replaceAllInnerInputClassName ||
                    cn(
                        widthClass,
                        inputFormCss(
                            props.overrideClasses,
                            props.mergeClasses,
                            noBottomMargin,
                            props.ciBorderColour
                        )
                    )
                }
                tabIndex={props.tabIndex || 0}
                aria-label={ariaLabel}
                {...omit({...props, ...{autocomplete}}, [
                    `noBottomMargin`,
                    `autocomplete`,
                    `classNameWrapper`,
                    `description`,
                    `mergeClasses`,
                    `topMargin`,
                    `ciBorderColour`,
                    `replaceAllInnerInputClassName`,
                    `widthClass`,
                ])}
            />
            <IfTruthy test={props.description}>
                <TinyText style={{alignSelf: `center`}} mergeClasses={cn(widthClass, `mt-4`)}>
                    {props.description}
                </TinyText>
            </IfTruthy>
        </div>
    );
};

CIFormInput.displayName = `CIFormInput`;
